import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useRef } from "react";
// Limits


const parameterLimits = {
  STP_INLET: { cod: 1500, bod: 300, tss: 300, ph: { min: 5.5, max: 9 } },
  STP_OUTLET: { cod: 50, bod: 10, tss: 10, ph: { min: 5.5, max: 9 } }
};

const ScadaChart = () => {

  const pdfRef = useRef();
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [LiveData, setLiveData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedParam, setSelectedParam] = useState("cod");
  const [stationFilter, setStationFilter] = useState("");
  const [deviceFilter, setDeviceFilter] = useState("");
  const [ulbFilter, setUlbFilter] = useState("");

  // Fetch API
  const fetchScadaData = async (from, to) => {
    try {
      setLoading(true);

      const res = await fetch(
        "https://urbangis.mp.gov.in/api/Station_Data/GetScadaDataDateWise",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fromDate: from,
            toDate: to,
            distCd: null,
            ulbCd: null,
            siteId: null
          })
        }
      );

      const data = await res.json();
      if (data.statusCode === 200 && Array.isArray(data.data))
        setLiveData(data.data);
      else setLiveData([]);

    } catch (err) {
      console.error(err);
      setLiveData([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = d =>
    d
      ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
      : null;

  useEffect(() => {
    if (fromDate && toDate)
      fetchScadaData(formatDate(fromDate), formatDate(toDate));
  }, [fromDate, toDate]);

  // ULB list
  const ulbList = useMemo(() => {
    return [...new Set(LiveData.map(d => d.ulB_Name).filter(Boolean))].sort();
  }, [LiveData]);

  // Device list based on ULB
  const deviceList = useMemo(() => {
    return [
      ...new Set(
        LiveData
          .filter(d => !ulbFilter || d.ulB_Name === ulbFilter)
          .map(d => d.deviceName)
          .filter(Boolean)
      )
    ].sort();
  }, [LiveData, ulbFilter]);

  // Reset device when ULB changes
  useEffect(() => {
    setDeviceFilter("");
  }, [ulbFilter]);

  // Filter Data
  const filteredData = useMemo(() => {
    return LiveData.filter(item => {
      const stationMatch = stationFilter
        ? item.stationName === stationFilter
        : true;

      const deviceMatch = deviceFilter
        ? item.deviceName === deviceFilter
        : true;

      const ulbMatch = ulbFilter
        ? item.ulB_Name === ulbFilter
        : true;

      return stationMatch && deviceMatch && ulbMatch;
    });
  }, [LiveData, stationFilter, deviceFilter, ulbFilter]);

  // Colored chart segments
  const getColoredSeries = (data, param) => {
    const series = [];
    let segment = { name: param.toUpperCase(), data: [], color: "#00FF00" };

    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      const value = item[param];
      const station = item.stationName;

      let isRed = false;

      if (param === "ph") {
        const { min, max } = parameterLimits[station]?.ph || { min: 0, max: 14 };
        if (value < min || value > max) isRed = true;
      } else {
        const limit = parameterLimits[station]?.[param];
        if (limit && value > limit) isRed = true;
      }

      if (
        (segment.color === "#00FF00" && isRed) ||
        (segment.color === "#FF0000" && !isRed)
      ) {
        if (segment.data.length) series.push(segment);
        segment = {
          name: param.toUpperCase(),
          data: [],
          color: isRed ? "#FF0000" : "#00FF00"
        };
      }

      segment.data.push({
        x: new Date(item.recordDateTime),
        y: value,
        isRed
      });
    }

    if (segment.data.length) series.push(segment);
    return series;
  };

  const chartSeries = getColoredSeries(filteredData, selectedParam);

  const chartOptions = {
    chart: { type: "line", background: "#1f2a44" },
    theme: { mode: "dark" },
    stroke: { curve: "smooth", width: 2 },
    markers: {
      size: 4,
      colors: chartSeries.flatMap(seg =>
        seg.data.map(d => (d.isRed ? "#FF0000" : "#00FF00"))
      )
    },
    xaxis: { type: "datetime" },
    title: {
      text: selectedParam.toUpperCase(),
      align: "center",
      style: { color: "#00FFFF" }
    },
    colors: chartSeries.map(seg => seg.color)
  };

  const sortedData = useMemo(
    () => [...filteredData].sort((a, b) => a.siteId - b.siteId),
    [filteredData]
  );
  const labelStyle = {
    color: "#00e5ff",
    fontSize: "14px",
    marginBottom: "6px",
    display: "block",
    fontWeight: "600",
    letterSpacing: "0.5px"
  };

  const inputStyle = {
    width: "100%",
    padding: "9px 10px",
    borderRadius: "8px",
    border: "1px solid #00e5ff",
    background: "#0a1a24",
    color: "#fff",
    fontSize: "14px",
    outline: "none"
  };
  const cellStyle = {
    padding: "9px",
    border: "1px solid #2f4f6f",
    fontSize: "13px"
    
  };


  /***** DOWNLOAD*******/
  const downloadPDF = async () => {
    const element = pdfRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

    pdf.addImage(imgData, "PNG", 0, 10, pdfWidth, pdfHeight);
    pdf.save("SCADA_DetailsReport.pdf");
  };

  return (
    <div  style={{
       
          background: "#18787d",
          border: "none",
          color: "white",
          borderRadius: "4px",
          fontWeight: "100",
          cursor: "pointer",
         margin:"10px;",
        }}  >
      <button
        onClick={downloadPDF}
        style={{
          float: "right",
          background: "#0c659c",
          border: "none",
          color: "white",
          borderRadius: "4px",
          fontWeight: "100",
          cursor: "pointer",
          marginBottom: "0px"
        }}
      >
        Download PDF
      </button>


      <div ref={pdfRef} style={{ background: "#0d1b2a", color: "white" }}>

        <h6 style={{ textAlign: "center" }}>
          Supervisory Control And Data Acquisition
        </h6>


        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: "5px",
            marginBottom: "25px",
            background: "linear-gradient(135deg,#0f2027,#203a43,#2c5364)",
            borderRadius: "14px",
            boxShadow: "0 4px 18px rgba(0,0,0,0.4)",
            alignItems: "end"
          }}
        >
          {/* From */}
          <div>
            <label style={labelStyle}> From Date </label>
            <DatePicker
              selected={fromDate}
              onChange={setFromDate}
              dateFormat="dd/MM/yyyy"
              customInput={<input style={inputStyle} />}
            />
          </div>

          {/* To */}
          <div>
            <label style={labelStyle}> To Date</label>
            <DatePicker
              selected={toDate}
              onChange={setToDate}
              dateFormat="dd/MM/yyyy"
              customInput={<input style={inputStyle} />}
            />
          </div>

          {/* ULB */}
          <div>
            <label style={labelStyle}>ULB</label>
            <select
              style={inputStyle}
              value={ulbFilter}
              onChange={e => setUlbFilter(e.target.value)}
            >
              <option value="">All</option>
              {ulbList.map(u => (
                <option key={u}>{u}</option>
              ))}
            </select>
          </div>

          {/* Device */}
          <div>
            <label style={labelStyle}>Device</label>
            <select
              style={inputStyle}
              value={deviceFilter}
              onChange={e => setDeviceFilter(e.target.value)}
            >
              <option value="">All</option>
              {deviceList.map(d => (
                <option key={d}>{d}</option>
              ))}
            </select>
          </div>

          {/* Station */}
          <div>
            <label style={labelStyle}>Station</label>
            <select
              style={inputStyle}
              value={stationFilter}
              onChange={e => setStationFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="STP_INLET">STP_INLET</option>
              <option value="STP_OUTLET">STP_OUTLET</option>
            </select>
          </div>

          {/* Parameter */}
          <div>
            <label style={labelStyle}>Parameter</label>
            <select
              style={inputStyle}
              value={selectedParam}
              onChange={e => setSelectedParam(e.target.value)}
            >
              <option value="cod">COD</option>
              <option value="bod">BOD</option>
              <option value="tss">TSS</option>
              <option value="ph">pH</option>
           
            </select>
          </div>
        </div>

        {/* CHART */}

        {filteredData.length > 0 && (
          <ReactApexChart
            options={chartOptions}
            series={chartSeries}
            type="line"
            height={400}
          />
        )}

        {/* TABLE */}
        <div style={{ textAlign: "center", fontSize: "12px", fontWeight: "600", color: "#00FFFF" }}>
          Tabular Data
        </div>
        {loading && <p>Loading...</p>}

        {!loading && sortedData.length > 0 && (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              background: "#1b263b",
              borderRadius: "10px",
              overflow: "hidden",
              boxShadow: "0 4px 14px rgba(0,0,0,0.4)"
            }}
          >
            <thead>
              <tr style={{ background: "#243a5e", color: "#00e5ff" }}>
                {[
                  "S.No", "Device", "ULB", "Site ID", "Station",
                  "BOD", "COD", "TSS", "pH", "Total Flow(MLD)", "Date"
                ].map((h, i) => (
                  <th
                    key={i}
                    style={{
                      padding: "10px",
                      border: "1px solid #2f4f6f",
                      fontWeight: "600",
                      fontSize: "14px"
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedData.map((item, i) => (
                <tr
                  key={i}
                  style={{
                    
                    color: "#fff",
                    transition: "0.3s",
                    background: i % 2 === 0 ? "#1b263b" : "#162034"
                  }}
                >
                  <td style={cellStyle}>{i + 1}</td>
                  <td style={cellStyle}>{item.deviceName}</td>
                  <td style={cellStyle}>{item.ulB_Name}</td>
                  <td style={cellStyle}>{item.siteId}</td>
                  <td style={cellStyle}>{item.stationName}</td>
                  <td style={cellStyle}>{item.bod}</td>
                  <td style={cellStyle}>{item.cod}</td>
                  <td style={cellStyle}>{item.tss}</td>
                  <td style={cellStyle}>{item.ph}</td>
                  {/* <td style={cellStyle}>{item.flow}</td> */}
                  <td style={cellStyle}> {item.flow_Total
    ? (item.flow_Total / 1000).toFixed(2)
    : "0.00"}</td>
                  <td style={cellStyle}>
                    {item.recordDateTime?.replace("T", " ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && sortedData.length === 0 && (
          <p style={{ color: "Yellow", textAlign: "center" }}>No Data Found</p>
        )}
      </div>
    </div>
  );
};

export default ScadaChart;